/**
 * The canonical empty state. Its props are unchanged — ~25 call sites still
 * pass `title` + `description` + `icon` — but under Flat Grammar 2027 it now
 * renders through `EmptyVisual`: the title becomes the one line, the
 * description is dropped, and only one action survives.
 *
 * New surfaces should reach for `EmptyVisual` directly.
 */

import type { Icon } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

import type { EmptyVisualAction } from './empty-states/EmptyVisual'
import type { ProxiedActionState } from './empty-states/useProxiedAction'
import type { MascotVariant } from './Mascot'
import { EmptyVisual } from './empty-states/EmptyVisual'
import {
  clickProxiedAction,
  useProxiedActionState,
} from './empty-states/useProxiedAction'
import { Mascot } from './Mascot'

export interface EmptyStateProps {
  title: string
  /** Kept for source compatibility — the grammar allows one line only. */
  description?: string
  icon: Icon
  actionLabel?: string
  onAction?: () => void
  actionSelector?: string
  actionDisabled?: boolean
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  secondaryActionSelector?: string
  filterActive?: boolean
  onClearFilter?: () => void
  fullScreen?: boolean
  className?: string
  mascot?: MascotVariant
  mascotSize?: 'sm' | 'md' | 'lg' | 'xl'
  /** 'sm' fits panel and card contexts where the default is too large. */
  size?: 'default' | 'sm'
}

export interface CompactEmptyStateProps {
  title?: string
  /** Kept for source compatibility — no longer rendered. */
  description?: string
  className?: string
}

/** Compact empty state for inline sections like sprint task lists. */
export function CompactEmptyState({
  title = 'No tasks yet',
  className,
}: CompactEmptyStateProps) {
  return (
    <EmptyVisual shape="rows" line={title} size="card" className={className} />
  )
}

/**
 * Fires `onAction`, falling back to clicking the selector's element. When
 * that element is a visible button (the page header's "New deal"), the
 * header already offers the action, so the empty state leaves it out
 * rather than show it twice. A selector with nothing to click is dropped.
 */
function selectorAction(
  label: string | undefined,
  onAction: (() => void) | undefined,
  selector: string | undefined,
  selectorState: ProxiedActionState,
  disabled?: boolean,
): EmptyVisualAction | undefined {
  if (!label || selectorState === 'shown') return undefined
  if (!onAction && (!selector || selectorState === 'absent')) return undefined
  return {
    label,
    disabled,
    onClick: () => {
      if (onAction) {
        onAction()
        return
      }
      if (selector) clickProxiedAction(selector)
    },
  }
}

export function EmptyState({
  title,
  icon,
  actionLabel,
  onAction,
  actionSelector,
  actionDisabled = false,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionSelector,
  filterActive,
  onClearFilter,
  fullScreen = false,
  className,
  mascot,
  mascotSize = 'lg',
  size = 'default',
}: EmptyStateProps) {
  const visualSize = size === 'sm' ? 'card' : 'page'
  const primaryState = useProxiedActionState(actionSelector)
  const secondaryState = useProxiedActionState(secondaryActionSelector)
  // A header showing the create button also carries the secondary (Import
  // sits in its overflow menu), so neither belongs in the empty state.
  const headerOwnsActions = primaryState === 'shown'

  // One action, in order of intent: the primary, then the secondary when it
  // stands alone, then the escape hatch out of an active filter.
  const action =
    selectorAction(
      actionLabel,
      onAction,
      actionSelector,
      primaryState,
      actionDisabled,
    ) ??
    (headerOwnsActions
      ? undefined
      : selectorAction(
          secondaryActionLabel,
          onSecondaryAction,
          secondaryActionSelector,
          secondaryState,
        )) ??
    (filterActive && onClearFilter
      ? { label: 'Clear filters', onClick: onClearFilter }
      : undefined)

  // The mascot is page-level first-run only, and only when asked for.
  if (mascot) {
    return (
      <div
        className={cn(
          'ev',
          visualSize === 'card' ? 'ev-card' : 'ev-page',
          fullScreen && 'ev-fullscreen',
          className,
        )}
      >
        <Mascot variant={mascot} size={mascotSize} />
        <p className="ev-line">{title}</p>
        {action && (
          <button
            type="button"
            className="ev-action"
            disabled={action.disabled}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )}
      </div>
    )
  }

  return (
    <EmptyVisual
      shape="icon"
      icon={icon}
      line={title}
      action={action}
      size={visualSize}
      tone={filterActive ? 'filtered' : 'quiet'}
      fullScreen={fullScreen}
      className={className}
    />
  )
}
